import React, { Component } from "react";
import _ from "underscore";
import PropTypes from "prop-types";
import Decorator from "@evertrue/client-decorator";
import { PromiseResolver } from "@evertrue/promise-resolver";
import { usePrevious } from "@evertrue/et-components";

const CachedContactsContext = React.createContext(() => {});

const useCachedContactByIdsResolver = (ids = []) => {
  const { contacts, idsLoading, fetchContactsIfNotCached, fetchContacts } = React.useContext(CachedContactsContext);
  const prev_ids = usePrevious(ids);

  React.useEffect(
    () => {
      const new_ids = _.difference(ids, prev_ids);
      if (new_ids.length) {
        fetchContactsIfNotCached(new_ids);
      }
    },
    [fetchContactsIfNotCached, ids, prev_ids]
  );

  return { contacts, idsLoading, fetchContacts };
};

const useCachedContactByIdResolver = id => {
  const { contacts, idsLoading, fetchContactsIfNotCached, fetchContacts } = React.useContext(CachedContactsContext);

  React.useEffect(
    () => {
      if (id) {
        fetchContactsIfNotCached([id]);
      }
    },
    [fetchContactsIfNotCached, id]
  );

  const contact = contacts[id];
  const loading = idsLoading[id];
  return { contact, loading, fetchContacts };
};

const CachedContactsResolver = props => {
  const { contact, loading } = useCachedContactByIdResolver(props.id);
  return props.children({ contact, loading });
};

CachedContactsResolver.propTypes = {
  id: PropTypes.number,
  children: PropTypes.func.isRequired,
};

class CachedContactsProvider extends Component {
  static propTypes = {
    fetchContactsById: PropTypes.func.isRequired,
  };

  idsFetchedOrInFlight = new Set();
  _isMounted = null;

  componentDidMount() {
    this._isMounted = true;
  }
  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState(...args) {
    if (this._isMounted) {
      this.setState(...args);
    }
  }

  toggleLoading = (contactIds = [], bool) => {
    this.safeSetState({
      idsLoading: contactIds.reduce((acc, cur) => {
        acc[cur] = bool;
        return acc;
      }, {}),
    });
  };

  fetchContacts = (contactIds = []) => {
    contactIds.forEach(id => this.idsFetchedOrInFlight.add(id));
    this.toggleLoading(contactIds, true);

    this.props.fetchContactsById({ contactIds }).then(({ items = [] } = {}) => {
      const contacts = items.map(Decorator.Contacts.parse).reduce((acc, cur) => {
        acc[cur.id] = cur;
        return acc;
      }, {});
      this.safeSetState(
        prevState => ({ contacts: { ...prevState.contacts, ...contacts } }),
        () => {
          this.toggleLoading(contactIds, false);
        }
      );
    });
  };

  fetchContactsIfNotCached = (contactIds = []) => {
    if (this.idsFetchedOrInFlight.size > 200) {
      this.idsFetchedOrInFlight = new Set();
    }
    const new_ids = contactIds.filter(id => !this.idsFetchedOrInFlight.has(id));
    if (new_ids.length) {
      this.fetchContacts(contactIds);
    }
  };

  addContactToCache = contact => {
    this.idsFetchedOrInFlight.add(contact.id);
    this.safeSetState(prev => ({ contacts: { ...prev.contacts, [contact.id]: Decorator.Contacts.parse(contact) } }));
  };

  state = {
    contacts: {},
    idsLoading: {},
    fetchContacts: this.fetchContacts,
    fetchContactsIfNotCached: this.fetchContactsIfNotCached,
    addContactToCache: this.addContactToCache,
  };
  render() {
    return <CachedContactsContext.Provider value={this.state}>{this.props.children}</CachedContactsContext.Provider>;
  }
}

// use promise resolver for diffing logic
const CachedContactsPreLoader = ({ contactIds }) => (
  <CachedContactsContext.Consumer>
    {({ fetchContactsIfNotCached }) => (
      <PromiseResolver promise={contactIds => fetchContactsIfNotCached(contactIds)} args={contactIds}>
        {() => null}
      </PromiseResolver>
    )}
  </CachedContactsContext.Consumer>
);

CachedContactsProvider.propTypes = {
  contactIds: PropTypes.array,
  poolId: PropTypes.number,
  children: PropTypes.node,
  promises: PropTypes.object,
};
const useCachedContactsContext = () => React.useContext(CachedContactsContext);

export {
  CachedContactsPreLoader,
  CachedContactsResolver,
  CachedContactsProvider,
  CachedContactsContext,
  useCachedContactByIdResolver,
  useCachedContactByIdsResolver,
  useCachedContactsContext,
};
